/**
 * Home's project strip — every site visible with its status; tap one to scope
 * (design: "Led AI Console Redesign", 16b).
 *
 * > Today the scope lives in two places (sidebar pill and the chip under the
 * > composer) and neither opens anything. […] "All projects" is always the
 * > first row so nobody gets stuck in one site. Projects carry what a foreman
 * > actually uses to tell them apart: last walk, what's waiting, and where it
 * > syncs.
 *
 * ## It replaced the Walks card, not the Recent walks grid
 *
 * Home used to put a project's own walk list here, under the pills. It was
 * the third list of walks on one page — the Walks card, the Recent walks grid
 * at the foot, and the Walks page a click away — and it only appeared at all
 * on a personal workspace with a filter already applied, which is precisely
 * the reader who least needs to be told which project they are in.
 *
 * What that slot is for is the question the reader actually arrives with:
 * WHICH project. The recent walks are still directly below (user, 2026-09-09).
 *
 * ## Ordering: most recently walked first
 *
 * Not alphabetical, which is what the sidebar picker uses and what
 * `listProjectCards` returns. A strip that shows three of eleven projects has
 * to choose which three, and "the ones you have been on" beats "the ones
 * starting with A" every time. Sites nobody has walked sort last, by name, so
 * a new project is reachable but never displaces a live one.
 *
 * ## Every tile is a link, including the one you are on
 *
 * The selected tile navigates too — back to `?site=` cleared, i.e. All
 * projects — because the reverse is a dead control, and this strip exists
 * exactly so that scope is never a place you can get stuck.
 */

import { useMemo, useState } from "react";
import type { ProjectCardRow } from "../api";
import { C, FONT_MONO, projectTile } from "../tokens";
import { dateWithYear } from "./chrome";

/** Two projects plus All projects keep the default strip to one three-card row. */
const COLLAPSED = 2;

export interface ProjectStripProps {
  projects: ProjectCardRow[];
  totals: { sites: number; toReview: number; pastDue: number };
  /** The `?site=` currently in force, or `null` for All projects. */
  activeSiteId: string | null;
  /** `null` clears the filter. */
  onPick: (siteId: string | null) => void;
  /** `null` clears the filter — used for the tile's own `href`, so a project
   *  can be opened in a new tab like any other link. */
  hrefFor: (siteId: string | null) => string;
  /** Fixed instant for "walked today". Tests pass a literal. */
  now?: Date;
}

export function ProjectStrip({
  projects,
  totals,
  activeSiteId,
  onPick,
  hrefFor,
  now,
}: ProjectStripProps) {
  const [expanded, setExpanded] = useState(false);

  const ordered = useMemo(() => {
    // A copy: the prop is the server's own alphabetical order and other
    // things on this page read it.
    return [...projects].sort((a, b) => {
      const left = a.lastWalkAt ? Date.parse(a.lastWalkAt) : 0;
      const right = b.lastWalkAt ? Date.parse(b.lastWalkAt) : 0;
      if (left !== right) return right - left;
      return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
    });
  }, [projects]);

  const today = useMemo(() => now ?? new Date(), [now]);

  const shown = expanded ? ordered : ordered.slice(0, COLLAPSED);

  if (projects.length === 0) return null;

  return (
    <div data-testid="project-strip" style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          // Match Home's Recent walks heading exactly: section titles share
          // the same left edge, weight, size, and optical rhythm.
          padding: "30px 2px 10px",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 19.55, fontWeight: 600, letterSpacing: "-0.25px" }}>
          Projects
        </h2>
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10.93,
            letterSpacing: "0.76px",
            textTransform: "uppercase",
            color: C.tileSource,
          }}
        >
          tap one to scope home
        </span>
        <span style={{ flex: 1 }} />
        {/* Only when there is something behind it — and the test is the
            PROJECT COUNT, not `hidden`, which is zero while expanded. A
            control that says "All 3" next to three tiles does nothing, and
            one that still says "Show fewer" after the list shrank under it
            does nothing either. */}
        {ordered.length > COLLAPSED && (
          <button
            type="button"
            className="kb"
            data-testid="project-strip-all"
            aria-expanded={expanded}
            onClick={() => setExpanded((was) => !was)}
            style={{
              border: "none",
              background: "transparent",
              padding: 0,
              font: "inherit",
              fontSize: 14.95,
              fontWeight: 500,
              color: C.amber,
              cursor: "pointer",
            }}
          >
            {expanded ? "Show fewer" : `All ${totals.sites}`}
          </button>
        )}
      </div>

      <div
        data-testid="project-strip-grid"
        // At most three columns, wrapping on narrow screens even when expanded.
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(max(190px, calc((100% - 20px) / 3)), 1fr))",
          gap: 10,
        }}
      >
        <AllProjectsTile
          totals={totals}
          selected={activeSiteId === null}
          href={hrefFor(null)}
          onPick={() => onPick(null)}
        />
        {shown.map((project) => (
          <ProjectTile
            key={project.id}
            project={project}
            today={today}
            selected={project.id === activeSiteId}
            href={hrefFor(project.id === activeSiteId ? null : project.id)}
            onPick={() => onPick(project.id === activeSiteId ? null : project.id)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiles
// ---------------------------------------------------------------------------

function tileShell(selected: boolean): React.CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 14,
    borderRadius: 14,
    minHeight: 118,
    boxSizing: "border-box",
    textAlign: "left",
    font: "inherit",
    cursor: "pointer",
    background: selected ? C.ink : C.surface,
    color: selected ? C.surface : C.ink,
    border: `1px solid ${selected ? C.ink : C.hair}`,
    ...(selected ? { boxShadow: `0 6px 18px ${C.tileShadow}` } : {}),
  };
}

function AllProjectsTile({
  totals,
  selected,
  href,
  onPick,
}: {
  totals: ProjectStripProps["totals"];
  selected: boolean;
  href: string;
  onPick: () => void;
}) {
  return (
    <a
      className="kb project-tile"
      data-testid="project-tile"
      data-site="all"
      data-selected={selected ? "true" : "false"}
      href={href}
      aria-current={selected ? "true" : undefined}
      onClick={(event) => {
        event.preventDefault();
        onPick();
      }}
      style={tileShell(selected)}
    >
      <span
        aria-hidden
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: selected ? C.tileOnMeta : C.tileGlyph,
          display: "grid",
          placeItems: "center",
        }}
      >
        <ListGlyph tone={selected ? C.ink : C.railScope} />
      </span>
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 17.25, fontWeight: 600, letterSpacing: "-0.15px" }}>
        All projects
      </span>
      <span style={{ fontSize: 14.37, color: selected ? C.tileOnMeta : C.tileMeta }}>
        {`${plural(totals.sites, "site", "sites")}${
          totals.toReview > 0 ? ` · ${totals.toReview} to review` : ""
        }`}
      </span>
    </a>
  );
}

function ProjectTile({
  project,
  today,
  selected,
  href,
  onPick,
}: {
  project: ProjectCardRow;
  today: Date;
  selected: boolean;
  href: string;
  onPick: () => void;
}) {
  const meta = selected ? C.tileOnMeta : C.tileMeta;
  return (
    <a
      className="kb project-tile"
      data-testid="project-tile"
      data-site={project.id}
      data-selected={selected ? "true" : "false"}
      href={href}
      aria-current={selected ? "true" : undefined}
      onClick={(event) => {
        event.preventDefault();
        onPick();
      }}
      style={tileShell(selected)}
      title={selected ? "Showing this project — open All projects" : `Scope Home to ${
        project.name || "this project"
      }`}
    >
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {/* The console's own project swatch, not the design's map thumbnail:
            there is no map. `projectTile` derives it from the id, so a
            project wears the same colour here as in the sidebar and on every
            row that names it. */}
        <span
          aria-hidden
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: projectTile(project.id),
            flex: "none",
          }}
        />
        {project.syncSource && (
          <span
            data-testid="project-tile-source"
            style={{
              fontFamily: FONT_MONO,
              fontSize: 10.35,
              letterSpacing: "0.72px",
              textTransform: "uppercase",
              color: selected ? C.tileOnMeta : C.tileSource,
            }}
          >
            {project.syncSource === "procore" ? "Procore" : "Autodesk"}
          </span>
        )}
      </span>
      <span style={{ flex: 1 }} />
      <span
        style={{
          fontSize: 17.25,
          fontWeight: 600,
          letterSpacing: "-0.15px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {project.name || "Unnamed project"}
      </span>
      <span data-testid="project-tile-status" style={{ fontSize: 14.37, color: meta }}>
        <ProjectStatus project={project} today={today} selected={selected} />
      </span>
    </a>
  );
}

/**
 * `Walked today · 4 to review` / `Aug 17 · 2 to review`.
 *
 * The parts are dropped when they are zero rather than printed as `0`: this
 * is a line about what needs doing, and a tile reading "0 to review" spends
 * words saying nothing. A project with none of it says when it was last
 * walked, which is the fact that is always true.
 */
function ProjectStatus({
  project,
  today,
  selected,
}: {
  project: ProjectCardRow;
  today: Date;
  selected: boolean;
}) {
  const parts: React.ReactNode[] = [];

  parts.push(
    <span key="walk">{project.lastWalkAt ? walkedLabel(project.lastWalkAt, today) : "Not walked yet"}</span>,
  );
  if (project.toReview > 0) {
    parts.push(
      <span
        key="review"
        // Gold on the selected tile — 16b's one accent, and it lands on the
        // number the reader is being asked to act on.
        style={selected ? { color: C.gold, fontWeight: 600 } : undefined}
      >
        {`${project.toReview} to review`}
      </span>,
    );
  }
  return (
    <>
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 && " · "}
          {part}
        </span>
      ))}
    </>
  );
}

/** `Walked today` / `Yesterday` / `Aug 17, 2026`. Compared as LOCAL days, never by
 *  subtracting instants: a walk at 8am and a clock at 7am the next morning
 *  are 23 hours apart and two different days. */
export function walkedLabel(iso: string, today: Date): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "Not walked yet";
  const day = new Date(at.getFullYear(), at.getMonth(), at.getDate());
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((midnight.getTime() - day.getTime()) / 86_400_000);
  if (days <= 0) return "Walked today";
  if (days === 1) return "Yesterday";
  return dateWithYear(at);
}

/** `"3 sites"` / `"1 site"`. `chrome`'s own `plural`, imported rather than
 *  re-spelled — kept local only to avoid a cycle through `chrome`'s view
 *  imports. */
function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function ListGlyph({ tone }: { tone: string }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M5.5 4h7M5.5 8h7M5.5 12h7M2.6 4h.01M2.6 8h.01M2.6 12h.01"
        stroke={tone}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
